import Joi from 'joi'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../../UI/Btn'
import Uploader from '../../../../../UI/Uploader'
import StatusIcon from '../../../../../UI/StatusIcon'

import clsx from 'clsx'
import http from '../../../../../shared/http'
import { openParentalTemplate } from '../../../../../shared/parentalTemplate'
import { joiFile } from '../../../../../shared/helpers'
import { schemaOptions } from '../../../../../shared/i18n'
import { DOCUMENT_TYPES, TApi } from '../../../../../shared/const'
import { verkleinereBild } from '../../../../../shared/shrinkImage'

import styles from '../style.module.scss'
import { addParentalConfirmation } from '../../../../../redux/ducks/user'
/* import { ReactComponent as RoundWarningIcon } from '../../../../../assets/icons/round-warning.svg' */
import { updateProfile } from '../../../../../redux/ducks/user'

const schema = Joi.object({
	parentalConfirmation: joiFile(10)
})

function ParentalConfirmation(props) {
	const { t } = useTranslation()

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors }
	} = useForm({
		resolver: joiResolver(schema, {
			errors: {
				labels: false,
				language: localStorage.getItem('i18nextLng')
			},
			...schemaOptions
		})
	})

	const [parentalConfirmationFileName, setParentalConfirmationFileName] = useState('')
	const [isLoading, setLoading] = useState(false)

	const [parentalConfirmationField] = watch(['parentalConfirmation'])

	useEffect(() => {
		const file = parentalConfirmationField && parentalConfirmationField[0]
		if (!file) return

		setParentalConfirmationFileName(file.name)
	}, [parentalConfirmationField])

	useEffect(() => {
		setParentalConfirmationFileName(props.parentalConfirmationFileName)
	}, [props.parentalConfirmationFileName])

	useEffect(() => {
		if (errors.parentalConfirmation) {
			setParentalConfirmationFileName(props.parentalConfirmationFileName)
		}
	}, [props.parentalConfirmationFileName, errors.parentalConfirmation])

	const handleChange = async fields => {
		const formData = new FormData()
		if (fields.parentalConfirmation && fields.parentalConfirmation[0])
			formData.append('parentalConfirmation', await verkleinereBild(fields.parentalConfirmation[0]))

		http.put(TApi.STUDENT_PARENTAL_CONFIRMATION, formData).then(({ data }) => {
			toast.success(t('Parental confirmation updated'))
			props.addParentalConfirmation(data)
			props.updateProfile({ rejectedParentalConfirmation: false })
		})
	}

	// Dieselbe Vorlage öffnet die Lehrkraft in ihrem Profil (shared/parentalTemplate).
	const parentalTemplate = () => {
		setLoading(true)
		openParentalTemplate(TApi.STUDENT_PARENTAL_CONFIRMATION_TEMPLATE).finally(() => {
			setLoading(false)
		})
	}

	/*
	 * Nach der Aktivierung ist eingefroren, was die Aktivierung trägt: eine
	 * **freigegebene** Einverständniserklärung. Sie ist eine Voraussetzung, und
	 * die neue Datei wäre ungeprüft — dieselbe Regel prüft der Server
	 * (services/documents/requirementDocuments).
	 *
	 * Alles andere bleibt offen: eine beanstandete Erklärung (Anforderung #90,
	 * Punkt 4) und ebenso die noch fehlende. Bis 23.08.2026 zählte hier allein
	 * die Beanstandung — wer nach der Aktivierung dazukam, sah ein gesperrtes
	 * Feld und konnte seine Erklärung nie einreichen. In der Lehrkräfte-Sicht auf
	 * ein fremdes Profil bleibt das Feld in jedem Fall gesperrt.
	 */
	const uploadGesperrt = props.isTeacherStudentView || (props.isActiveCompany && props.reviewedParentalConfirmation)

	// Fehlt die Erklärung, tritt die ganze Karte hervor (Anforderung #182) —
	// dieselbe Bedingung wie das Warnzeichen und shared/missingDocuments.
	const fehlt = !props.isParentalConfirmation || props.rejectedParentalConfirmation

	return (
		<form className={styles.root}>
			<div className={clsx(styles.section, fehlt && styles.sectionMissing)}>
				<div className={styles.parentalTitle}>
					<h1 className={styles.parentalTitleText}>{t('Consent of parent or guardian')}</h1>
					{fehlt && (
						/* Strich-SVG statt der PNG-Grafik `warning.png` — dieselbe
						   Warnung wie in den Listen, in der Farbe des Textes. */
						<StatusIcon
							name="warning"
							size={24}
							className={styles.warningParentalConfirmation}
							title={
								props.rejectedParentalConfirmation
									? t('Parental confirmation was rejected, upload new please !')
									: t('Need to upload parental confirmation')
							}
						/>
					)}
				</div>
				<div className={styles.sectionContent}>
					<div className={styles.flexContainer}>
						<div className={styles.parentalConfirmation}>
							<Uploader
								className={styles.field}
								classNameLabel={
									uploadGesperrt
										? styles.disabledLabel
										: props.isParentalConfirmation && !props.rejectedParentalConfirmation
										? styles.fieldDone
										: styles.fieldPending
								}
								disabled={uploadGesperrt}
								onChange={handleSubmit(handleChange)}
								error={errors.parentalConfirmation}
								accept="application/pdf,image/jpeg,image/png,image/gif"
								nameFile={parentalConfirmationFileName}
								title={t('Parental Confirmation')}
								register={register('parentalConfirmation')}
							/>
						</div>
						{!props.isTeacherStudentView && (
							<div className={styles.docActions}>
								<Btn
									loading={isLoading}
									className={styles.btnWithoutIcon}
									icon="download"
									iconSize={{ w: 16, h: 16 }}
									iconColor="#fff"
									onClick={() => parentalTemplate()}
								>
									{t('Download template')}
								</Btn>
							</div>
						)}
					</div>
				</div>
			</div>
		</form>
	)
}

ParentalConfirmation.propTypes = {
	parentalConfirmationFileName: PropTypes.string.isRequired,
	isActiveCompany: PropTypes.bool.isRequired,
	isParentalConfirmation: PropTypes.bool.isRequired,
	isTeacherStudentView: PropTypes.bool.isRequired,
	rejectedParentalConfirmation: PropTypes.bool,
	reviewedParentalConfirmation: PropTypes.bool
}

const mapStateToProps = state => ({
	isActiveCompany: state.user.profile.EmploymentCompany?.isActive,
	parentalConfirmationFileName:
		state.user.profile.Documents &&
		state.user.profile.Documents.find(({ type }) => type === DOCUMENT_TYPES.PARENTAL_CONFIRMATION)?.originalName,
	isParentalConfirmation: state.user.profile.Documents?.find(
		({ type }) => type === DOCUMENT_TYPES.PARENTAL_CONFIRMATION
	),
	rejectedParentalConfirmation: state.user.profile.rejectedParentalConfirmation,
	reviewedParentalConfirmation: state.user.profile.reviewedParentalConfirmation
})

const mapDispatchToProps = dispatch => ({
	addParentalConfirmation: data => dispatch(addParentalConfirmation(data)),
	updateProfile: data => dispatch(updateProfile(data))
})
export default connect(mapStateToProps, mapDispatchToProps)(ParentalConfirmation)
